import { gsap } from 'gsap';

type HeroCarouselConfig = {
  rootSelector: string;
  slideSelector: string;
  contentSelector: string;
  prevButtonSelector: string;
  nextButtonSelector: string;
  indicatorSelector: string;
  layerSelector: string;
  overlaySelector: string;
  contentInnerSelector: string;
  noImageSlideClass: string;
  activeNoImageClass: string;
  activeWithImageClass: string;
  duration: number;
  layerStagger: number;
  scaleStagger: number;
  ease: string;
  textExitStart: number;
  textEnterStart: number;
};

type HeroCarouselOptions = Partial<HeroCarouselConfig>;

const defaultConfig: HeroCarouselConfig = {
  rootSelector: '.js-carousel-hero',
  slideSelector: '.js-carousel-hero-slide',
  contentSelector: '.js-carousel-hero-content',
  prevButtonSelector: '.carousel-hero__button--prev',
  nextButtonSelector: '.carousel-hero__button--next',
  indicatorSelector: '.js-carousel-hero-indicator',
  layerSelector: '.js-carousel-hero-layer',
  overlaySelector: '.carousel-hero__overlay',
  contentInnerSelector: '.carousel-hero__content-inner',
  noImageSlideClass: 'carousel-hero__slide--no-image',
  activeNoImageClass: 'carousel-hero--active-no-image',
  activeWithImageClass: 'carousel-hero--active-with-image',
  duration: 1.45,
  layerStagger: 0,
  scaleStagger: 0,
  ease: 'power2.inOut',
  textExitStart: 0,
  textEnterStart: 1.45 * 0.65,
};

const isElement = (el: HTMLElement | null): el is HTMLElement => el !== null;

// Promote a set of elements to compositor layers just before they animate,
// then release them immediately after to avoid holding GPU memory permanently.
const setWillChange = (els: HTMLElement[], value: string): void => {
  els.forEach((el) => {
    el.style.willChange = value;
  });
};

function initCarousel(parent: HTMLElement, config: HeroCarouselConfig): void {
  const slides = Array.from(
    parent.querySelectorAll<HTMLElement>(config.slideSelector)
  );
  const contents = Array.from(
    parent.querySelectorAll<HTMLElement>(config.contentSelector)
  );
  const prevBtn = parent.querySelector<HTMLButtonElement>(
    config.prevButtonSelector
  );
  const nextBtn = parent.querySelector<HTMLButtonElement>(
    config.nextButtonSelector
  );
  const indicators = Array.from(parent.querySelectorAll<HTMLButtonElement>(
    config.indicatorSelector
  ));

  if (slides.length === 0) return;

  const setActiveTheme = (index: number): void => {
    const isNoImage = slides[index].classList.contains(config.noImageSlideClass);
    parent.classList.toggle(config.activeNoImageClass, isNoImage);
    parent.classList.toggle(config.activeWithImageClass, !isNoImage);
  };

  if (slides.length <= 1) {
    setActiveTheme(0);
    return;
  }

  let activeIndex = 0;
  let isAnimating = false;
  const autoRotateDelay = () => {
    if (parent.dataset.timer && parent.dataset.timer !== "") {
      const timeInSeconds = Number(parent.dataset.timer);

      if (Number.isNaN(timeInSeconds) || timeInSeconds <= 0 || timeInSeconds === Infinity) return 6000;
      return timeInSeconds * 1000;
    }

    return 6000;
  }
  let autoRotateTimer: number | undefined;

  slides.forEach((slide, i) => {
    const layers = Array.from(
      slide.querySelectorAll<HTMLElement>(config.layerSelector)
    );
    const imgs = layers
      .map((l) => l.querySelector<HTMLElement>('img'))
      .filter(isElement);
    const overlay = slide.querySelector<HTMLElement>(config.overlaySelector);

    gsap.set(slide, { zIndex: i === 0 ? 2 : 1 });
    gsap.set(layers, {
      clipPath: i === 0 ? 'inset(0 0% 0 0)' : 'inset(0 100% 0 0)',
    });
    if (imgs.length > 0) {
      gsap.set(imgs, { scale: i === 0 ? 1 : 1.2 });
    }
    if (overlay) {
      gsap.set(overlay, { opacity: i === 0 ? 1 : 0 });
    }
  });

  contents.forEach((content, i) => {
    const inner = content.querySelector<HTMLElement>(config.contentInnerSelector);
    if (i !== 0 && inner) {
      gsap.set(inner, { y: 0, opacity: 0 });
    }
  });

  const updateIndicator = (): void => {
    indicators.forEach((indicator, index) => {
      const isActive = index === activeIndex;
      indicator.classList.toggle('is-active', isActive);
      indicator.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  };

  updateIndicator();
  setActiveTheme(0);

  const goToSlide = (nextIndex: number): void => {
    if (isAnimating || nextIndex === activeIndex) return;
    isAnimating = true;

    const prevIndex = activeIndex;
    const currentSlide = slides[prevIndex];
    const nextSlide = slides[nextIndex];
    const currentContent = contents[prevIndex];
    const nextContent = contents[nextIndex];

    const currentLayers = Array.from(
      currentSlide.querySelectorAll<HTMLElement>(config.layerSelector)
    );
    const nextLayers = Array.from(
      nextSlide.querySelectorAll<HTMLElement>(config.layerSelector)
    );

    const currentImgs = currentLayers
      .map((l) => l.querySelector<HTMLElement>('img'))
      .filter(isElement);
    const nextImgs = nextLayers
      .map((l) => l.querySelector<HTMLElement>('img'))
      .filter(isElement);

    const currentInner = currentContent.querySelector<HTMLElement>(
      config.contentInnerSelector
    );
    const nextInner = nextContent.querySelector<HTMLElement>(
      config.contentInnerSelector
    );
    const nextOverlay = nextSlide.querySelector<HTMLElement>(config.overlaySelector);
    const currentOverlay = currentSlide.querySelector<HTMLElement>(
      config.overlaySelector
    );

    setWillChange([...currentLayers, ...nextLayers], 'clip-path');
    setWillChange([...currentImgs, ...nextImgs], 'transform');
    if (nextOverlay) nextOverlay.style.willChange = 'opacity';
    if (currentOverlay) currentOverlay.style.willChange = 'opacity';
    if (currentInner) currentInner.style.willChange = 'transform, opacity';
    if (nextInner) nextInner.style.willChange = 'transform, opacity';

    gsap.set(nextLayers, { clipPath: 'inset(0 100% 0 0)' });
    if (nextImgs.length > 0) gsap.set(nextImgs, { scale: 1.2 });
    gsap.set(nextSlide, { zIndex: 5 });

    if (nextOverlay) gsap.set(nextOverlay, { opacity: 0 });

    gsap.set(nextContent, { visibility: 'visible' });
    if (nextInner) gsap.set(nextInner, { y: 80, opacity: 0 });

    const tl = gsap.timeline({
      onComplete() {
        gsap.set(currentSlide, { zIndex: 1 });
        gsap.set(nextSlide, { zIndex: 2 });

        gsap.set(currentLayers, { clipPath: 'inset(0 0% 0 0)' });
        if (currentImgs.length > 0) gsap.set(currentImgs, { scale: 1 });
        if (currentOverlay) gsap.set(currentOverlay, { opacity: 0 });

        setWillChange([...currentLayers, ...nextLayers], 'auto');
        setWillChange([...currentImgs, ...nextImgs], 'auto');
        if (nextOverlay) nextOverlay.style.willChange = 'auto';
        if (currentOverlay) currentOverlay.style.willChange = 'auto';
        if (currentInner) currentInner.style.willChange = 'auto';
        if (nextInner) nextInner.style.willChange = 'auto';

        gsap.set(currentContent, { visibility: 'hidden' });
        if (currentInner) gsap.set(currentInner, { y: 0, opacity: 0 });

        isAnimating = false;

        currentContent.setAttribute('aria-hidden', 'true');
        nextContent.removeAttribute('aria-hidden');
      },
    });

    tl.call(() => {
      activeIndex = nextIndex;
      setActiveTheme(nextIndex);
      updateIndicator();
    }, [], config.duration * 0.4);

    if (currentImgs.length > 0) {
      tl.to(
        currentImgs,
        {
          scale: 1.2,
          duration: config.duration,
          stagger: config.scaleStagger,
          ease: config.ease,
        },
        0
      );
    }

    if (nextImgs.length > 0) {
      tl.to(
        nextImgs,
        {
          scale: 1,
          duration: config.duration,
          stagger: config.scaleStagger,
          ease: config.ease,
        },
        0
      );
    }

    nextLayers.forEach((layer, idx) => {
      tl.to(
        layer,
        {
          clipPath: 'inset(0 0% 0 0)',
          duration: config.duration,
          ease: config.ease,
        },
        idx * config.layerStagger
      );
    });

    if (nextOverlay) {
      tl.to(
        nextOverlay,
        { opacity: 1, duration: config.duration * 0.8, ease: 'power1.inOut' },
        config.layerStagger
      );
    }

    if (currentInner) {
      tl.to(
        currentInner,
        {
          y: -80,
          opacity: 0,
          duration: config.duration * 0.6,
          ease: config.ease,
        },
        config.textExitStart
      );
    }

    if (nextInner) {
      tl.to(
        nextInner,
        {
          y: 0,
          opacity: 1,
          duration: config.duration * 0.6,
          ease: config.ease,
        },
        config.textEnterStart
      );
    }
  };

  const startAutoRotate = (): void => {
    if (autoRotateTimer !== undefined) {
      globalThis.clearInterval(autoRotateTimer);
    }

    autoRotateTimer = globalThis.setInterval(() => {
      goToSlide((activeIndex + 1) % slides.length);
    }, autoRotateDelay());
  };

  const resetAutoRotate = (): void => {
    startAutoRotate();
  };

  prevBtn?.addEventListener('click', () => {
    goToSlide((activeIndex - 1 + slides.length) % slides.length);
    resetAutoRotate();
  });

  nextBtn?.addEventListener('click', () => {
    goToSlide((activeIndex + 1) % slides.length);
    resetAutoRotate();
  });

  indicators.forEach((indicator, index) => {
    indicator.addEventListener('click', () => {
      goToSlide(index);
      resetAutoRotate();
    });
  });

  // ── Touch / swipe support ────────────────────────────────────────────────
  // Minimum horizontal distance (px) required to register as a swipe.
  // Keeps accidental swipes during vertical scrolling from triggering slides.
  const SWIPE_THRESHOLD = 50;
  let touchStartX = 0;
  let touchStartY = 0;

  parent.addEventListener('touchstart', (e: TouchEvent) => {
    touchStartX = e.changedTouches[0].screenX;
    touchStartY = e.changedTouches[0].screenY;
  }, { passive: true });

  parent.addEventListener('touchend', (e: TouchEvent) => {
    const dx = e.changedTouches[0].screenX - touchStartX;
    const dy = e.changedTouches[0].screenY - touchStartY;

    // Ignore if vertical movement dominates (user is scrolling, not swiping)
    if (Math.abs(dy) > Math.abs(dx)) return;
    if (Math.abs(dx) < SWIPE_THRESHOLD) return;

    if (dx < 0) {
      // Swipe left → next slide
      goToSlide((activeIndex + 1) % slides.length);
    } else {
      // Swipe right → previous slide
      goToSlide((activeIndex - 1 + slides.length) % slides.length);
    }

    resetAutoRotate();
  }, { passive: true });

  startAutoRotate();
}

export const initHeroCarouselAnimation = (
  options: HeroCarouselOptions = {}
): void => {
  const config = {
    ...defaultConfig,
    ...options,
  };

  const elements = document.querySelectorAll<HTMLElement>(config.rootSelector);
  elements.forEach((parent) => initCarousel(parent, config));
};
